import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Strip HTML tags from a string
 * Preserves whitespace between blocks for readability
 */
export function stripHtml(html: string): string {
  if (!html) return '';

  // Replace block-level elements with spaces to preserve word boundaries
  let text = html
    .replace(/<(p|div|br|h[1-6]|li|tr|td|th)[^>]*>/gi, ' ')
    .replace(/<\/?(p|div|br|h[1-6]|li|tr|td|th)[^>]*>/gi, ' ');

  // Remove remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');

  // Decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  text = textarea.value;

  // Normalize whitespace (multiple spaces/newlines to single space)
  text = text.replace(/\s+/g, ' ').trim();

  return text;
}

/**
 * Count characters in a string (Unicode-aware)
 * Uses Intl.Segmenter for accurate emoji and multibyte character counting
 * Fixes Admin Columns issue #791 - proper multibyte character handling
 */
export function countCharacters(text: string): number {
  if (!text) return 0;

  // Use Intl.Segmenter if available (modern browsers)
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
    return [...segmenter.segment(text)].length;
  }

  // Fallback: spread operator handles most multibyte correctly
  // (handles emoji, Japanese, Arabic, etc.)
  return [...text].length;
}

/**
 * Truncate text to a maximum character count (Unicode-aware)
 * Adds ellipsis if truncated
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text) return '';

  const charCount = countCharacters(text);
  if (charCount <= maxLength) return text;

  // Use Intl.Segmenter if available
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
    const segments = [...segmenter.segment(text)];
    return segments.slice(0, maxLength).map(s => s.segment).join('') + '...';
  }

  // Fallback: spread operator
  return [...text].slice(0, maxLength).join('') + '...';
}

/**
 * Decode HTML entities in a string
 * Converts entities like &#8211; to their character equivalents
 * Uses browser's built-in HTML parser for accuracy
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return '';

  // Quick check: if no entities, return as-is
  if (!text.includes('&')) return text;

  // Use textarea element to decode entities (handles all standard HTML entities)
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}
